import type { Item } from '../db/types';
import { formatModified } from '../lib/dates';
import { previewText } from '../lib/markdown';
import { excerpt } from '../lib/search';
import { Checkbox } from './Checkbox';
import { Marked } from './TaskRow';
import './Row.css';

interface Props {
  item: Item;
  selected?: boolean;
  images?: number;
  /** Hide the leading square — the mobile Notes tab has no tasks to disambiguate from. */
  bare?: boolean;
  terms?: string[];
  onOpen?: () => void;
  onDelete?: () => void;
}

/** Title, first-line preview, modified date, image-count indicator. */
export function NoteRow({ item, selected = false, images = 0, bare = false, terms, onOpen, onDelete }: Props) {
  const preview = terms?.length ? excerpt(item.body, terms) : previewText(item.body, 80);

  return (
    <div
      className={['row', 'row--tall', selected && 'is-selected'].filter(Boolean).join(' ')}
      role="option"
      aria-selected={selected}
      tabIndex={-1}
      onClick={onOpen}
    >
      {!bare && <Checkbox kind="note" />}

      <div className="row__body">
        <div className="row__title">{terms ? <Marked text={item.title} terms={terms} /> : item.title}</div>
        {preview && (
          <div className="row__meta">
            {terms ? <Marked text={preview} terms={terms} /> : preview}
          </div>
        )}
      </div>

      {images > 0 ? (
        <span className="row__count">{images} img</span>
      ) : (
        <span className="row__meta" style={{ flex: 'none', color: 'var(--text-muted)' }}>
          {formatModified(item.updated_at)}
        </span>
      )}

      {onDelete && (
        <div className="row__actions">
          <button type="button" className="row__action" title={item.deleted ? 'Delete permanently' : 'Delete'} onClick={(e) => { e.stopPropagation(); onDelete(); }}>
            ⌫
          </button>
        </div>
      )}
    </div>
  );
}
